import {
	formatPrimaryPhasePercentValue,
	getPrimaryPhaseMetricLabel,
	isPrimaryPhaseCurrencyMetric,
	isPrimaryPhasePercentMetric,
	ORDER_PRIMARY_PHASE_METRICS,
} from '../../pipeline/lib/orderPrimaryPhaseMetric';

export const TREND_Y_AXIS_METRIC = {
	COUNT: ORDER_PRIMARY_PHASE_METRICS.COUNT,
	TOTAL_PRICE: ORDER_PRIMARY_PHASE_METRICS.TOTAL_PRICE,
	PROFIT_RATE: ORDER_PRIMARY_PHASE_METRICS.PROFIT_RATE,
};

export const TREND_MAX_Y_AXIS_METRICS = 2;

/** 趋势纵轴仅保留流量型指标；应收类为时点余额，不适合按时段对比 */
export const TREND_Y_AXIS_METRIC_OPTIONS = [
	{ value: ORDER_PRIMARY_PHASE_METRICS.COUNT, label: '单数' },
	{ value: ORDER_PRIMARY_PHASE_METRICS.TOTAL_PRICE, label: '合同金额' },
	{ value: ORDER_PRIMARY_PHASE_METRICS.PROFIT_RATE, label: '利润率' },
];

const VALID_METRIC_VALUES = new Set(TREND_Y_AXIS_METRIC_OPTIONS.map((item) => item.value));

export const TREND_Y_AXIS_METRIC_DISPLAY_ORDER = TREND_Y_AXIS_METRIC_OPTIONS.map((item) => item.value);

export const DEFAULT_TREND_Y_AXIS_METRICS = [TREND_Y_AXIS_METRIC.COUNT];

const PERIOD_CELL_FIELD_BY_METRIC = {
	[ORDER_PRIMARY_PHASE_METRICS.COUNT]: 'count',
	[ORDER_PRIMARY_PHASE_METRICS.TOTAL_PRICE]: 'totalPrice',
	[ORDER_PRIMARY_PHASE_METRICS.PROFIT_RATE]: 'profitRate',
};

const resolveMetricScaleKind = (metric) => {
	if (metric === ORDER_PRIMARY_PHASE_METRICS.COUNT) return 'count';
	if (isPrimaryPhasePercentMetric(metric)) return 'rate';
	return 'amount';
};

export const sortLineCompareYAxisMetrics = (metrics = []) =>
	TREND_Y_AXIS_METRIC_DISPLAY_ORDER.filter((metric) => metrics.includes(metric));

export const isValidLineCompareYAxisMetric = (metric) => VALID_METRIC_VALUES.has(metric);

export const normalizeTrendYAxisMetrics = (input) => {
	let raw = input;
	if (input && typeof input === 'object' && !Array.isArray(input)) {
		raw = input.trendYAxisMetrics ?? input.trendYAxisMetric;
	}
	if (typeof raw === 'string' && raw.trim()) {
		raw = [raw];
	}
	if (!Array.isArray(raw)) {
		return [...DEFAULT_TREND_Y_AXIS_METRICS];
	}
	const next = [];
	for (const metric of raw) {
		if (!isValidLineCompareYAxisMetric(metric) || next.includes(metric)) continue;
		next.push(metric);
		if (next.length >= TREND_MAX_Y_AXIS_METRICS) break;
	}
	return next.length ? sortLineCompareYAxisMetrics(next) : [...DEFAULT_TREND_Y_AXIS_METRICS];
};

export const resolvePrimaryTrendYAxisMetric = (metrics) =>
	normalizeTrendYAxisMetrics(metrics)[0] ?? TREND_Y_AXIS_METRIC.COUNT;

export const toggleLineCompareYAxisMetric = (activeMetrics, metric) => {
	if (!isValidLineCompareYAxisMetric(metric)) return normalizeTrendYAxisMetrics(activeMetrics);
	const normalized = normalizeTrendYAxisMetrics(activeMetrics);
	if (normalized.includes(metric)) {
		if (normalized.length <= 1) return normalized;
		return sortLineCompareYAxisMetrics(normalized.filter((item) => item !== metric));
	}
	if (normalized.length >= TREND_MAX_Y_AXIS_METRICS) return normalized;
	return sortLineCompareYAxisMetrics([...normalized, metric]);
};

export const resolveTrendPeriodValueField = (metric) => PERIOD_CELL_FIELD_BY_METRIC[metric] ?? 'count';

export const resolveLineCompareMetricBindings = (metrics = []) => {
	const sorted = normalizeTrendYAxisMetrics(metrics);
	const kinds = sorted.map((metric) => resolveMetricScaleKind(metric));
	const useDualAxis = new Set(kinds).size > 1;
	const leftKind = kinds[0];

	return sorted.map((metric, index) => {
		const kind = kinds[index];
		return {
			metric,
			label: getPrimaryPhaseMetricLabel(metric),
			currencyMetric: isPrimaryPhaseCurrencyMetric(metric),
			percentMetric: isPrimaryPhasePercentMetric(metric),
			yAxisIndex: useDualAxis ? (kind === leftKind ? 0 : 1) : 0,
			valueField: resolveTrendPeriodValueField(metric),
		};
	});
};

export const resolveLineCompareYAxisLabels = (bindings = []) => {
	const left = bindings.find((item) => item.yAxisIndex === 0);
	const right = bindings.find((item) => item.yAxisIndex === 1);
	const useDualAxis = Boolean(right);
	return {
		useDualAxis,
		leftName: useDualAxis ? left?.label ?? '' : '',
		rightName: useDualAxis ? right?.label ?? '' : '',
	};
};

export const resolveTrendMetricAxisFormat = (binding) => {
	if (!binding) return {};
	if (binding.percentMetric) {
		return {
			currencyMetric: false,
			formatAxisValue: formatPrimaryPhasePercentValue,
			allowFraction: true,
		};
	}
	return {
		currencyMetric: Boolean(binding.currencyMetric),
		allowFraction: Boolean(binding.currencyMetric),
	};
};

export const formatTrendYAxisMetricsLabel = (metrics = [], separator = ' / ') =>
	normalizeTrendYAxisMetrics(metrics)
		.map((metric) => getPrimaryPhaseMetricLabel(metric))
		.join(separator);
